/**
 * Algorithm 3: Ebbinghaus Forgetting Curve (PRD §1.1)
 *
 * Model:  R = e^(-t / S)
 *   R  = retention probability (0.0–1.0)
 *   t  = days since last successful recall
 *   S  = memory stability (grows with each correct recall; resets on failure)
 *
 * Review is scheduled when R drops below RETENTION_THRESHOLD (0.80).
 * This drives QUESTION SELECTION priority — it does NOT mutate stored scores.
 * Score changes only when the learner answers a question.
 */

import { EBBINGHAUS } from '@config/constants.js';

/**
 * Compute the current retention probability for a question.
 *
 * @param {number|null} lastSuccessfulRecallMs - Timestamp (ms) of most recent correct answer
 * @param {number} stabilityS - Memory stability value (S); use getStabilityForRecallCount()
 * @returns {number} Retention probability 0.0–1.0 (1.0 = perfectly retained)
 */
export function computeRetentionProbability(lastSuccessfulRecallMs, stabilityS) {
  if (!lastSuccessfulRecallMs || stabilityS <= 0) return 0;
  const daysSince = (Date.now() - lastSuccessfulRecallMs) / 86_400_000;
  return Math.exp(-daysSince / stabilityS);
}

/**
 * Map a learner's correct-recall count to a memory stability value (S).
 * Uses the PRD §1.2 default interval progression.
 *
 * @param {number} timesCorrect - Number of successful recalls so far
 * @returns {number} Stability value in days
 */
export function getStabilityForRecallCount(timesCorrect) {
  if (timesCorrect <= 0) return 1;
  const prog = EBBINGHAUS.STABILITY_PROGRESSION;
  const idx = Math.min(timesCorrect - 1, prog.length - 1);
  return prog[idx];
}

/**
 * Compute the next memory stability value after an answer attempt.
 *
 * On correct: advance S to the next interval tier (PRD §1.2).
 * On incorrect: reduce S by STABILITY_FAILURE_FACTOR (PRD §1.2 — "reduce by 30%").
 *
 * @param {number} timesCorrectAfterThisAnswer - timesCorrect already incremented for correct answers
 * @param {boolean} wasCorrect
 * @param {number} currentStability - Existing S value (used as floor baseline on failure)
 * @returns {number} New stability value
 */
export function computeNextStability(timesCorrectAfterThisAnswer, wasCorrect, currentStability = 1) {
  if (!wasCorrect) {
    return Math.max(1, currentStability * EBBINGHAUS.STABILITY_FAILURE_FACTOR);
  }
  return getStabilityForRecallCount(timesCorrectAfterThisAnswer);
}

/**
 * Return true if the question is overdue for review (R has dropped below 0.80).
 * Used by AdaptiveEngine to prioritise the review queue (PRD §1.4).
 *
 * @param {number|null} lastSuccessfulRecallMs
 * @param {number} stabilityS
 * @returns {boolean}
 */
export function isReviewDue(lastSuccessfulRecallMs, stabilityS) {
  return computeRetentionProbability(lastSuccessfulRecallMs, stabilityS) < EBBINGHAUS.RETENTION_THRESHOLD;
}
